import {
  useRef, useState, useEffect, useMemo,
} from 'react';
import { Range, getTrackBackground } from 'react-range';
import { useTheme } from 'styled-components';
import { hasValidTimeInterval, parseToDateTimeString } from './helpers';
import { ThemeType } from '../../../../theme/themeType';

const LENGTH = 12;
const DAY_IN_MS = 86399999;
const MAX_ZOOM = 20;
const ZOOM_IN_FACTOR = 1.3;
const ZOOM_OUT_FACTOR = 0.77;

type Props = {
  currentDate: Date;
  timeRanges: number[][];
  errorMsg?: string;
  periodSelection?: boolean;
  duration?: number;
  onRangeSelect?: (from: string, to: string) => void;
};

function RangeSlider({
  currentDate, timeRanges, errorMsg, periodSelection = true, duration = 0, onRangeSelect,
}: Readonly<Props>): JSX.Element | null {
  const theme = useTheme() as ThemeType;
  const absMin = currentDate.getTime();
  const absMax = currentDate.getTime() + DAY_IN_MS;

  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomCenter, setZoomCenter] = useState(absMin + DAY_IN_MS / 2);

  const viewSpan = DAY_IN_MS / zoomLevel;
  const min = Math.max(absMin, zoomCenter - viewSpan / 2);
  const max = Math.min(absMax, min + viewSpan);

  const values = useMemo(
    () => timeRanges
      .filter(([s, e]) => s <= max && e >= min)
      .flatMap(([s, e]) => [Math.max(s, min), Math.min(e, max)]),
    [timeRanges, min, max],
  );

  const colorsConfig = useMemo(
    () => [theme.colors.outline].concat(
      Array.from(
        { length: (values.length / 2) * 2 },
        () => [theme.colors.inactive, theme.colors.outline],
      ).flat(),
    ),
    [values.length],
  );

  const trackRef = useRef<HTMLDivElement>(null);
  const minRef = useRef(min);
  const maxRef = useRef(max);
  minRef.current = min;
  maxRef.current = max;

  const [dragStart, setDragStart] = useState<number | null>(null);
  const [selectionMs, setSelectionMs] = useState<[number, number] | null>(null);

  const timeRangesRef = useRef(timeRanges);
  timeRangesRef.current = timeRanges;

  useEffect(() => {
    if (!periodSelection && duration > 0) {
      setSelectionMs((prev) => {
        if (!prev) return null;
        const slot = timeRangesRef.current.find(([s, e]) => prev[0] >= s && prev[0] <= e);
        const rawEnd = prev[0] + duration * 1000;
        return [prev[0], slot ? Math.min(rawEnd, slot[1]) : rawEnd];
      });
    }
  }, [duration, periodSelection]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const cursorMs = Math.round(minRef.current + ratio * (maxRef.current - minRef.current));
      setZoomLevel((prev) => Math.min(MAX_ZOOM, Math.max(1, prev * (e.deltaY < 0 ? ZOOM_IN_FACTOR : ZOOM_OUT_FACTOR))));
      setZoomCenter(cursorMs);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const msFromEvent = (e: React.MouseEvent): number => {
    if (!trackRef.current) return min;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    return Math.round(min + ratio * (max - min));
  };

  const clampToAvailable = (ms: number): boolean => (
    timeRanges.some(([s, e]) => ms >= s && ms <= e)
  );

  const clampRangeToSlot = (start: number, end: number): [number, number] | null => {
    const slot = timeRanges.find(([s, e]) => start >= s && start <= e);
    if (!slot) return null;
    return [start, Math.min(end, slot[1])];
  };

  const resolveRange = (startMs: number, cursorMs: number): [number, number] => {
    if (!periodSelection && duration > 0) {
      return [startMs, startMs + duration * 1000];
    }
    return [Math.min(startMs, cursorMs), Math.max(startMs, cursorMs)];
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    const ms = msFromEvent(e);
    if (!clampToAvailable(ms)) return;
    setDragStart(ms);
    setSelectionMs(resolveRange(ms, ms));
  };

  const handleMouseMove = (e: React.MouseEvent): void => {
    if (dragStart === null) return;
    const ms = msFromEvent(e);
    const [start, end] = resolveRange(periodSelection ? dragStart : ms, ms);
    if (periodSelection) {
      const clamped = clampRangeToSlot(start, end);
      if (clamped) setSelectionMs(clamped);
    } else {
      setSelectionMs([start, end]);
    }
  };

  const handleMouseUp = (e: React.MouseEvent): void => {
    if (dragStart === null) return;
    const ms = msFromEvent(e);
    const [start, end] = resolveRange(periodSelection ? dragStart : ms, ms);
    setDragStart(null);
    const clamped = clampRangeToSlot(start, end);
    if (!clamped || !onRangeSelect) return;
    onRangeSelect(
      parseToDateTimeString(new Date(clamped[0]), true),
      parseToDateTimeString(new Date(clamped[1]), true),
    );
  };

  const viewSpanMs = max - min;
  const visibleSelection = selectionMs
    ? [Math.max(selectionMs[0], min), Math.min(selectionMs[1], max)] as const
    : null;
  const selectionLeft = visibleSelection ? `${((visibleSelection[0] - min) / viewSpanMs) * 100}%` : null;
  const selectionWidth = visibleSelection ? `${((visibleSelection[1] - visibleSelection[0]) / viewSpanMs) * 100}%` : null;

  const legendStep = viewSpanMs / LENGTH;
  const legendTimes = Array.from({ length: LENGTH + 1 }, (_, i) => (
    parseToDateTimeString(new Date(min + i * legendStep))
  ));

  return (
    <div className="time-range">
      {errorMsg && (
        <p className="timeline-error">{errorMsg}</p>
      )}
      <Range
        disabled
        values={values}
        min={min}
        max={max}
        onChange={() => {}}
        renderTrack={({ props, children }) => (
          <div
            ref={(node) => {
              (trackRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }}
            role="presentation"
            onMouseDown={onRangeSelect ? handleMouseDown : props.onMouseDown}
            onMouseMove={onRangeSelect ? handleMouseMove : undefined}
            onMouseUp={onRangeSelect ? handleMouseUp : undefined}
            onMouseLeave={onRangeSelect ? () => setDragStart(null) : undefined}
            onTouchStart={props.onTouchStart}
            style={{
              ...props.style,
              display: 'flex',
              width: '100%',
              cursor: onRangeSelect ? 'crosshair' : 'default',
            }}
          >
            <div
              ref={props.ref}
              className="scale"
              style={{
                background: getTrackBackground({
                  values, colors: colorsConfig, min, max,
                }),
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {children}
              {selectionMs && selectionLeft && selectionWidth && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: selectionLeft,
                    width: selectionWidth,
                    minWidth: '3px',
                    height: '100%',
                    backgroundColor: theme.colors.main_cta,
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </div>
        )}
        renderThumb={({ index, props: thumbProps }) => {
          const chunkStart = values[index % 2 === 0 ? index : index - 1];
          const chunkEnd = values[index % 2 === 0 ? index + 1 : index];
          const minLabelIntervalMs = (max - min) * 0.08;
          const isLastThumb = index === values.length - 1;
          const isEndThumb = index % 2 === 1;
          const chunkWideEnough = hasValidTimeInterval(chunkStart, chunkEnd, minLabelIntervalMs);
          const showLabel = chunkWideEnough || (isLastThumb && isEndThumb);
          const prevValue = values[index - 1];
          const tooCloseToLeft = isLastThumb && !chunkWideEnough
            && prevValue !== undefined
            && (values[index] - prevValue) < minLabelIntervalMs;
          return (
            <div
              {...thumbProps}
              style={{
                ...thumbProps.style,
                height: '24px',
                width: '4px',
                borderRadius: '12px',
                backgroundColor: theme.colors.main_cta,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {showLabel && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-25px',
                    color: theme.colors.main_cta,
                    fontWeight: 'bold',
                    fontSize: '12px',
                    fontFamily: 'Arial,Helvetica Neue,Helvetica,sans-serif',
                    padding: '4px',
                    borderRadius: '4px',
                    backgroundColor: theme.colors.outline,
                    left: tooCloseToLeft ? '8px' : undefined,
                  }}
                >
                  {parseToDateTimeString(new Date(values[index]))}
                </div>
              )}
            </div>
          );
        }}
      />
      <div className="legend">
        {legendTimes.map((time, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span className="label" key={i}>{time}</span>
        ))}
      </div>
      {onRangeSelect && (
        <div
          style={{
            fontSize: '11px',
            color: theme.colors.inactive,
            marginTop: '4px',
            textAlign: 'center',
          }}
        >
          Scroll to zoom in / out
        </div>
      )}
    </div>
  );
}

export default RangeSlider;
